import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import NakshatraAnalysis from "./NakshatraAnalysis";
import { TRIMURTI_COLORS, getChartSlices } from "./NakshatraColorMapping";

interface CustomProps {
  data: any;
  planets: any[];
}

const Trimurti = (props: CustomProps) => {
  const { t } = useTranslation();
  const [data1, setData1] = useState<any[]>([]);
  const [data2, setData2] = useState<any[]>([]);
  const [data3, setData3] = useState<any[]>([]);
  const [data4, setData4] = useState<any[]>([]);

  const getTableHeaders = () => {
    return {
      tableHeader1: [
        {
          name: `${t("planetsTitle")}`,
          field: "point_name",
        },
        {
          name: `${t("nakshatraTitle")}`,
          field: "nakshatra",
        },
        {
          name: `${t("trimurtiTitle")}`,
          field: "nakshatra_trimurthi",
        },
      ],
      tableHeader2: [
        {
          name: `${t("trimurtiNakshatraTitle")}`,
          field: "key",
        },
        {
          name: `${t("percentageTrimurtiTitle")}`,
          field: "value",
        },
      ],
      tableHeader3: [
        {
          name: `${t("trimurtiNakshatraTitle")}`,
          field: "key",
        },
        {
          name: `${t("countTrimurtiTitle")}`,
          field: "value",
        },
      ],
    };
  };

  const trimurtiMapping: any = {
    Brahma: " (Create)",
    Vishnu: " (Maintain)",
    Shiva: " (Dissolve)",
  };

  useEffect(() => {
    convertDataToTableFormat(props.data);
  }, [props.data, props.planets]);

  const convertDataToTableFormat = (data: any) => {
    const tableValue = data
      .filter((item: any) => props.planets.includes(item.point_name))
      .map((item: any) => ({
        point_name: item.point_name,
        nakshatra: `${item.nakshatra_key}.${item.name}`,
        nakshatra_trimurthi:
          capitalizeFirstLetter(item.nakshatra_trimurthi) +
          trimurtiMapping[capitalizeFirstLetter(item.nakshatra_trimurthi)],
      }));
    setData1(tableValue);
    percentageCalculation(tableValue);
  };

  const percentageCalculation = (data: any) => {
    const temperamentCounts: any = {};

    data.forEach((item: any) => {
      const temperament = item.nakshatra_trimurthi;
      if (temperamentCounts[temperament] === undefined) {
        temperamentCounts[temperament] = 0;
      }
      temperamentCounts[temperament]++;
    });
    const totalCount = data.length;
    const percentages: any = {};

    Object.keys(temperamentCounts).forEach((temperament) => {
      percentages[temperament] =
        (temperamentCounts[temperament] / totalCount) * 100;
    });

    const temperamentPercentageData = Object.keys(percentages).map(
      (temperament) => ({
        key: temperament,
        value: `${percentages[temperament].toFixed(2)} %`,
        percentage: percentages[temperament].toFixed(2),
      })
    );

    setData2(temperamentPercentageData);
    setData3(getMaxObjects(temperamentPercentageData));

    const formattedData = [
      ["key", "value"],
      ...temperamentPercentageData.map((item) => [
        item.key,
        parseFloat(item.percentage),
      ]),
    ];
    setData4(formattedData);
  };

  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const getMaxObjects = (data: any[]) => {
    const max = Math.max(...data.map((item) => +item.percentage));
    return data.filter((item) => +item.percentage === max);
  };

  const getTrimurti = () => {
    const headers = getTableHeaders();
    return (
      <NakshatraAnalysis
        chartTitle={"trimurtiNakshatraTitle"}
        chartData={data4}
        chartSlices={getChartSlices(data4, TRIMURTI_COLORS)}
        tableHeader1={headers.tableHeader1}
        tableData1={data1}
        tableHeader2={headers.tableHeader2}
        tableData2={data2}
        tableHeader3={headers.tableHeader3}
        tableData3={data3}
        placeColor="#000000"
      />
    );
  };

  return getTrimurti();
};

export default Trimurti;
